/**
 * ChatMessage — renders one chat message (user or assistant), including the rich quote/booking CARDS.
 *
 * The agents service returns plain text; when it includes a `DISPLAY_CARD:fullDetails={json}` marker
 * (plus optional trailing text like "Any special instructions?"), this component parses it into a
 * styled card and renders the trailing text as its own message bubble. See components/README.md.
 *
 * Layout:
 *   - token helpers            detectRouteRequest / detectActions / stripRouteToken / stripActionsToken /
 *                              stripFlightInfoToken / stripAgentTokens (strip [[…]] markup, surface chips).
 *   - parseFullDetails(content) THE parser: a DISPLAY_CARD string -> { cards, trailing } (handles a
 *                              single object, an array, and trailing text after the JSON).
 *   - format helpers           formatDateTime, formatLocation.
 *   - card components          TripBlock, SummaryRow, QuoteSummaryCard, BookingSummaryCard, ErrorCard.
 *   - FeedbackControl          the thumbs up/down on an assistant message.
 *   - ChatMessage (default)    the component: parse -> render card(s) in one bubble + trailing as its own.
 */
import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import { StatusBadge } from '../ui/primitives';
import { IconBot, IconWarning } from '../ui/icons';
import { ThumbsUp, ThumbsDown, Star, MapPin, ChevronRight, Plus, Send, Pencil, ArrowRight } from 'lucide-react';
import { cx } from '../ui/cx';
import { CharterQuoteCard } from '../charter/CharterQuoteCard';
import { ConfirmationCard } from '../charter/ConfirmationCard';
import { RouteCard } from '../charter/RouteCard';

// Safe renderers for assistant markdown. Assistant content is influenceable by the customer's own turns
// (prompt injection) and by the other party during a live-agent handoff, so the customer bubble must not:
//   - auto-fetch a remote <img> (a silent IP/coarse-geo leak + a path-encoded exfil channel), or
//   - render a bare <a> that opens with window.opener access or points anywhere non-http(s).
// Images render as their alt text; links are http(s)-only, open in a new tab, and can't reach back (noopener).
const MD_COMPONENTS = {
  img: ({ alt }) => <span className="opacity-70">{alt ? `🖼 ${alt}` : '🖼 [image]'}</span>,
  a: ({ href, children }) => {
    const safe = typeof href === 'string' && /^https?:\/\//i.test(href);
    return safe
      ? <a href={href} target="_blank" rel="noopener noreferrer nofollow">{children}</a>
      : <span>{children}</span>;
  },
};

// Agent appends [[FLIGHT_INFO_FORM airport=XXX]] (airport optional) when asking
// for the four pickup-airport flight fields. We strip the token from the visible text.
const FLIGHT_INFO_TOKEN_RE = /\s*\[\[FLIGHT_INFO_FORM(?:\s+airport=([A-Za-z]{3}))?\]\]\s*/i;

export function stripFlightInfoToken(content) {
  if (!content) return content;
  return content.replace(FLIGHT_INFO_TOKEN_RE, ' ').replace(/\s{2,}/g, ' ').trim();
}

// Peoria charter only: the agent appends [[ROUTE_FORM]] when it asks for a leg's exact route —
// [[ROUTE_FORM:legN]] for a multi-city itinerary's Nth leg (N>=2), which opens ANOTHER (single-leg)
// form whose confirm fills that leg's leg{N}_* slots. (:two_leg was a short-lived variant; still
// stripped, treated as leg 1, for deploy skew.) The web surfaces an in-flow "Set your route on a map"
// button (the verified-locations popup) instead of a plain text ask. We strip the token from the
// visible text here; CustomerApp imports detectRouteRequest to render the button and open the right leg.
// :multi may carry a PRE-SEED payload — [[ROUTE_FORM:multi={"legs":[…]}]] — so the form opens filled in
// with the itinerary the customer described (or the latest legs after a chat edit). The seed JSON is
// compact and never contains "]]", so the non-greedy match up to the terminating "]]" is safe.
// :shuttle (bare or ={"legs":[…]}-seeded, same legs[] shape) opens the recurring-shuttle variant of the
// modern single-leg form (RouteFormMulti's `shuttle` variant — see CustomerApp.js).
const ROUTE_FORM_TOKEN_RE = /\s*\[\[ROUTE_FORM(:one_way(?:=[\s\S]*?)?|:round_trip(?:=[\s\S]*?)?|:shuttle(?:=[\s\S]*?)?|:leg\d+|:two_leg|:return|:multi(?:=[\s\S]*?)?)?\]\]\s*/i;

export function detectRouteRequest(content) {
  if (!content) return null;
  const m = ROUTE_FORM_TOKEN_RE.exec(content);
  if (!m) return null;
  const tag = m[1] || '';
  // :multi -> the consolidated MULTI-LEG form (all legs at once, add/remove/reorder — confirm sends a
  // legs[] token); an optional ={json} seeds it PRE-FILLED. :return -> the round trip's ride-home form
  // (fills return_*); :legN -> a multi-city itinerary's Nth leg (fills leg{N}_*); anything else -> leg 1.
  if (/:multi/i.test(tag)) {
    const eq = tag.indexOf('=');
    if (eq >= 0) {
      try {
        const seed = JSON.parse(tag.slice(eq + 1));
        if (seed && Array.isArray(seed.legs)) {
          return { leg: 'multi', legs: seed.legs };
        }
      } catch (e) { /* malformed seed -> fall back to the empty form */ }
    }
    return { leg: 'multi' };
  }
  // :round_trip -> the DEDICATED two-leg round-trip form (Outbound + Return, fixed at two legs, no
  // add-leg; the return ends at the original departure). An optional ={outbound,return,…} payload
  // PRE-SEEDS both legs. Confirm sends an {outbound,return,passengerCount,coachDisposition} token.
  if (/:round_trip/i.test(tag)) {
    const eq = tag.indexOf('=');
    if (eq >= 0) {
      try {
        const seed = JSON.parse(tag.slice(eq + 1));
        if (seed && (seed.outbound || seed.return)) return { leg: 'round_trip', roundTrip: seed };
      } catch (e) { /* malformed seed -> fall back to the empty round-trip form */ }
    }
    return { leg: 'round_trip' };
  }
  if (/:return/i.test(tag)) return { leg: 'return' };
  // :one_way -> the isolated one-way itinerary form (single origin->destination leg + optional stops,
  // confirmed with "Confirm itinerary"). An optional ={legs:[…]} payload PRE-SEEDS it with the trip
  // (city/state, venue labels, date/time, stops) so the form opens filled in.
  if (/:one_way/i.test(tag)) {
    const eq = tag.indexOf('=');
    if (eq >= 0) {
      try {
        const seed = JSON.parse(tag.slice(eq + 1));
        if (seed && Array.isArray(seed.legs)) return { leg: 'one_way', legs: seed.legs };
      } catch (e) { /* malformed seed -> fall back to the empty one-way form */ }
    }
    return { leg: 'one_way' };
  }
  // :shuttle -> the recurring-shuttle route form (the one-way single-leg layout relabelled: shuttle
  // pickup point / drop-off point / first service day & first pickup time). An optional ={legs:[…]}
  // payload PRE-SEEDS it with the loop's endpoints + first service day (same leg shape as :one_way).
  if (/:shuttle/i.test(tag)) {
    const eq = tag.indexOf('=');
    if (eq >= 0) {
      try {
        const seed = JSON.parse(tag.slice(eq + 1));
        if (seed && Array.isArray(seed.legs)) return { leg: 'shuttle', legs: seed.legs };
      } catch (e) { /* malformed seed -> fall back to the empty shuttle form */ }
    }
    return { leg: 'shuttle' };
  }
  const legMatch = /:leg(\d+)/i.exec(tag);
  return { leg: legMatch ? parseInt(legMatch[1], 10) : 1 };
}

// Peoria charter: find the ACTIVE route ask — the most recent assistant message carrying a
// [[ROUTE_FORM…]] token — scanning BACKWARDS through the thread, not just the last message. A rider
// who types while the form is pending pushes the token-bearing bubble up the thread; if only the last
// message were checked, the "Set your route" chip would vanish with no way back (the live "boi"
// stranded-rider bug — the agent's steer said "open the form" but nothing on screen could open it).
// The scan STOPS (returns none) at the first form-confirm receipt (a user message flagged
// routeReceipt/route by the form's onConfirm) or at any assistant quote card: an ask that PRE-DATES a
// confirm or a card must never win — routeAsk's seed outranks the session's confirmed route in
// CustomerApp's multiSeed/shuttleSeed, and post-confirm/post-card turns have their own affordances
// (the card's "Edit itinerary" / a fresh seeded token from the agent).
// Returns { index, leg, legs, roundTrip } with index === -1 when there is no active ask.
export function findRouteAsk(messages) {
  const none = { index: -1, leg: 1, legs: null, roundTrip: null };
  if (!Array.isArray(messages)) return none;
  for (let i = messages.length - 1; i >= 0; i -= 1) {
    const m = messages[i];
    if (!m) continue;
    if (m.role === 'user' && (m.routeReceipt || m.route)) return none;   // confirm receipt — ask spent
    if (m.role !== 'assistant' || !m.content) continue;
    if (hasCharterCard(m.content)) return none;                          // card turns own their edits
    const ask = detectRouteRequest(m.content);
    if (ask) {
      return { index: i, leg: ask.leg || 1, legs: ask.legs || null, roundTrip: ask.roundTrip || null };
    }
  }
  return none;
}

// Peoria charter only: the agent appends [[ACTIONS=a,b,c]] listing the CONTEXTUAL action chips valid for
// this turn (planner-driven, so we never render an action that doesn't apply yet). detectActions returns
// the ordered id list; CustomerApp maps each click to the matching behavior (see ACTION handlers there).
// This reduces dependence on perfect intent classification — a click sends the exact phrase the
// deterministic charter planner already recognizes (or opens the route form for an edit).
const ACTIONS_TOKEN_RE = /\s*\[\[ACTIONS=([a-z_,\s]*)\]\]\s*/i;

export function detectActions(content) {
  if (!content) return null;
  const m = content.match(ACTIONS_TOKEN_RE);
  if (!m) return null;
  const ids = m[1].split(',').map((s) => s.trim()).filter(Boolean);
  return ids.length ? ids : null;
}

export function stripActionsToken(content) {
  if (!content) return content;
  return content.replace(ACTIONS_TOKEN_RE, ' ').replace(/\s{2,}/g, ' ').trim();
}

// Chip presentation for each action id. `send` = a canned phrase the planner recognizes (handled in
// CustomerApp.onCharterAction); `edit` = open the verified-location route form (no message sent).
// A recurring-shuttle session gets shuttle-vocabulary ids from the agent (same behaviors, labels the
// shuttle flow's own copy actually uses — it says "pickup spots" and "route", never "stops"/"itinerary").
export const CHARTER_ACTION_CHIPS = {
  continue: { label: 'Continue', Icon: ArrowRight },
  edit_itinerary: { label: 'Edit itinerary', Icon: Pencil },
  add_stop: { label: 'Add stop', Icon: Plus },
  edit_route: { label: 'Edit route', Icon: Pencil },        // shuttle: opens the shuttle route form
  add_pickup: { label: 'Add pickup spot', Icon: Plus },     // shuttle: sends "add a pickup spot"
  send_request: { label: 'Send request', Icon: Send },
};

// Peoria charter: a cheap check for whether an assistant message renders a charter quote card.
// Used by CustomerApp to gate the "Edit route" affordance to the LATEST quote card only. The
// feasibilityVerdict field is charter-card-specific; false negatives just hide the link (safe).
export function hasCharterCard(content) {
  if (!content || typeof content !== 'string') return false;
  if (!/DISPLAY_CARD:/i.test(content) && !content.includes('fullDetails=')) return false;
  // A customer charter card strips feasibilityVerdict, so it can't be the sole signal (that gap left
  // the "Edit itinerary" button off single-leg cards). editSeed + the Q-CHTR quoteNumber + the
  // multi-leg cardType are charter-only and cheap to string-test — mirrors isCharterCard().
  return /feasibilityVerdict/.test(content)
    || /"editSeed"/.test(content)
    || /"cardType"\s*:\s*"charter_trip"/.test(content)
    || /"quoteNumber"\s*:\s*"Q-CHTR/i.test(content);
}

// Peoria charter: pull the itinerary SEED (`editSeed` = route-form legs) out of a rendered quote card
// so "Edit itinerary" reopens the ONE route form PRE-FILLED for EVERY trip type (one-way / round-trip /
// 2-leg / multi), not just the multi-leg case. Returns the legs[] seed, or null if the card has none.
export function extractCharterEditSeed(content) {
  const parsed = parseFullDetails(content);
  if (!parsed || !Array.isArray(parsed.cards)) return null;
  for (const c of parsed.cards) {
    const seed = c && c.data && c.data.editSeed;
    if (Array.isArray(seed) && seed.length) return seed;
  }
  return null;
}

// Peoria charter: the card's trip TYPE ('one_way' / 'round_trip' / 'multi_leg' / 'special_request')
// — the badge's source of truth. Used so "Edit itinerary" on a ONE-WAY card reopens the ONE-WAY form
// (single leg, no round-trip toggle / add-another-leg), the same form the rider edited all along.
export function extractCharterTripType(content) {
  const parsed = parseFullDetails(content);
  if (!parsed || !Array.isArray(parsed.cards)) return null;
  for (const c of parsed.cards) {
    const tt = c && c.data && c.data.tripType;
    if (tt) return tt;
  }
  return null;
}

// Peoria charter: the round-trip card's shared meta (passenger count + coach-disposition note) — NOT
// carried in editSeed (a legs[] array), so "Edit itinerary" on a round-trip card needs it separately to
// re-seed the form's passenger count (required to confirm) and the between-trips note.
export function extractCharterCardMeta(content) {
  const parsed = parseFullDetails(content);
  if (!parsed || !Array.isArray(parsed.cards)) return null;
  for (const c of parsed.cards) {
    const d = c && c.data;
    if (d && (d.passengerCount != null || d.coachDisposition != null)) {
      return { passengerCount: d.passengerCount != null ? String(d.passengerCount) : '',
               coachDisposition: d.coachDisposition || '' };
    }
  }
  return null;
}

// Peoria charter: true when an assistant message is an error/failure the rider is stuck on — used by
// CustomerApp to surface the "switch to the quote form" prompt. Covers the three shapes the chat can
// show an error in: a REJECTED:: card marker (parseFullDetails), the onError fallback
// ("Sorry, something went wrong: …"), and the graceful compose fallback ("I ran into a problem …").
const ERROR_MESSAGE_RE =
  /REJECTED::|something went wrong|ran into a problem|couldn't process|please try again/i;

export function isErrorMessage(content) {
  if (!content || typeof content !== 'string') return false;
  return ERROR_MESSAGE_RE.test(content);
}

export function stripRouteToken(content) {
  if (!content) return content;
  return content.replace(ROUTE_FORM_TOKEN_RE, ' ').replace(/\s{2,}/g, ' ').trim();
}

// Belt-and-suspenders scrub of ALL internal markup so a token can never reach a rendered transcript
// (customer OR operator/dispatch). Runs the specific route/flight strippers, then a general [[…]] sweep
// (catches any future/variant marker), then collapses a leftover DISPLAY_CARD line. The server already
// strips these before persisting, but the operator TranscriptPanel renders stored text raw, so this is
// the defense-in-depth on the render side.
export function stripAgentTokens(content) {
  if (!content) return content;
  return stripRouteToken(stripFlightInfoToken(String(content)))
    .replace(/\[\[[\s\S]*?]]/g, ' ')
    .replace(/^DISPLAY_CARD:[\s\S]*$/i, '[quote presented]')
    .replace(/\s{2,}/g, ' ')
    .trim();
}

// Best-effort repair of a TRUNCATED JSON object/array string. The streamed quote card can lose its
// tail when the final SSE event is dropped mid-stream, leaving e.g.
//   {"quoteNumber":"Q-...", ... ,"pricingDisclaimer":"Instant estimate. DOT feas
// balanceJSON closes the open string/brackets/braces (and drops a dangling partial field); salvageJSON
// then trims back field-by-field until it parses. Because the cut is always at the TAIL, every
// important field (total, route, feasibility) is present, so the card still renders — only the cut
// trailing field is lost. Returns the parsed value or null.
function balanceJSON(s) {
  let inStr = false, esc = false;
  const closers = [];
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (esc) { esc = false; continue; }
    if (c === '\\') { esc = true; continue; }
    if (inStr) { if (c === '"') inStr = false; continue; }
    if (c === '"') inStr = true;
    else if (c === '{') closers.push('}');
    else if (c === '[') closers.push(']');
    else if (c === '}' || c === ']') closers.pop();
  }
  let out = s;
  if (inStr) out += '"';                              // close an open string value/key
  out = out.replace(/:\s*$/, ': null');               // key with no value yet
  out = out.replace(/,\s*$/, '');                     // dangling comma
  out = out.replace(/,\s*"(?:[^"\\]|\\.)*"$/, '');    // dangling "key" with no colon/value
  for (let i = closers.length - 1; i >= 0; i--) out += closers[i];
  return out;
}

function salvageJSON(raw) {
  if (typeof raw !== 'string' || (raw[0] !== '{' && raw[0] !== '[')) return null;
  let s = raw;
  for (let guard = 0; guard < 4000 && s.length > 1; guard++) {
    try { return JSON.parse(balanceJSON(s)); } catch { /* trim to the previous field and retry */ }
    const cut = Math.max(s.lastIndexOf(','), s.lastIndexOf('}'), s.lastIndexOf(']'));
    if (cut <= 0) break;
    s = s.slice(0, cut);
  }
  return null;
}

// Trailing text after a card is meant to be the follow-up PROSE (a confirm / next question). Route
// mode can re-emit a partial copy of the card's JSON, which otherwise lands here and renders as raw
// text under the card. Drop anything that looks like JSON debris so the user never sees it.
function cleanTrailing(t) {
  if (!t) return null;
  const s = String(t).trim();
  if (!s) return null;
  if (/^[,{}[\]"]/.test(s)) return null;                          // starts like JSON
  if (s.includes('fullDetails=') || /DISPLAY_CARD:/i.test(s)) return null;
  if (/"\s*:\s*("|-?\d|true|false|null|\[|\{)/.test(s)) return null; // contains a "key": value pair
  if (/\\u[0-9a-fA-F]{4}/.test(s)) return null;                   // raw unicode-escaped JSON
  return s;
}

// Map a parsed card object to its card type (shared by the intact + salvaged paths).
// Charter recognition must NOT rely on feasibilityVerdict alone: the customer card strips it (and the
// single-leg card never sets cardType:'charter_trip'), so a one-way/round-trip charter quote arrives
// carrying only its Q-CHTR quoteNumber + editSeed. Missing those signals is exactly what left the
// bubble stuck on "Putting your estimate together…". editSeed and the Q-CHTR prefix are charter-only,
// so they cleanly separate a charter quote from a rideshare quote (which has trips[] / quoteId).
function isCharterCard(data) {
  return data.cardType === 'charter_trip'
    || !!data.feasibilityVerdict
    || data.editSeed != null
    || /^Q-CHTR/i.test(data.quoteNumber || '');
}
function cardTypeOf(data) {
  return data.cardType === 'submission_confirmation' ? 'confirmation'
    : data.bookingId ? 'booking'
    : isCharterCard(data) ? 'charter'
    : (data.quoteId || data.quoteNumber) ? 'quote' : null;
}

// A charter quote chains its post-quote follow-up ("add a stop? ready to send?", the DOT alternative,
// the next question) INSIDE the card JSON (quote.followupPrompt) so it can't be dropped as fragile
// trailing prose. We surface it here as the trailing bot message so it renders as an ordinary message
// BELOW the card, never inside it. Returns null when no charter card carries a prompt.
function charterFollowup(items) {
  const c = items.find((it) => it.type === 'charter' && it.data && it.data.followupPrompt);
  return c ? c.data.followupPrompt : null;
}

function parseFullDetails(content) {
  if (!content) return null;

  // Check for REJECTED:: error messages
  const rejectedMatch = content.match(/REJECTED::(.+)/i);
  if (rejectedMatch) {
    const errorMsg = rejectedMatch[1].trim();
    // Try to extract JSON error detail
    const jsonMatch = errorMsg.match(/\{.*"error"\s*:\s*"([^"]+)"/);
    const displayMsg = jsonMatch ? jsonMatch[1] : errorMsg.replace(/^\d+\s*/, '').replace(/^\{.*\}\s*$/, errorMsg);
    return { cards: [{ type: 'error', data: { message: displayMsg } }], trailing: null };
  }

  // Try bare JSON array or object
  const trimmed = content.trim();
  if ((trimmed.startsWith('[') || trimmed.startsWith('{')) &&
      (trimmed.includes('"quoteId"') || trimmed.includes('"quoteNumber"') || trimmed.includes('"bookingId"') || trimmed.includes('"bookingNumber"'))) {
    try {
      const parsed = JSON.parse(trimmed);
      const arr = Array.isArray(parsed) ? parsed : [parsed];
      if (arr.length > 0) {
        const items = arr
          .map(data => {
            // Normalize flattened agent format into card format
            if (data.pickup && !data.trips) {
              const normalized = {
                quoteId: data.quoteId || 0,
                quoteNumber: data.quoteNumber || '',
                status: data.status || 'DRAFT',
                currencyCode: data.currencyCode || 'USD',
                totalAmount: data.totalAmount || 0,
                subtotalAmount: data.totalAmount || 0,
                taxAmount: 0, feeAmount: 0, discountAmount: 0,
                trips: [{
                  tripSequence: 1,
                  pickup: {
                    addressLine1: null,
                    city: data.pickup?.city || null,
                    state: data.pickup?.state || null,
                    datetime: data.pickup?.datetime || null,
                    isAirport: data.pickup?.isAirport || false,
                    airportCode: data.pickup?.airportCode || null,
                    airline: null, flightNumber: null, terminal: null, flightTime: null,
                  },
                  dropoff: {
                    addressLine1: null,
                    city: data.dropoff?.city || null,
                    state: data.dropoff?.state || null,
                    datetime: null,
                    isAirport: data.dropoff?.isAirport || false,
                    airportCode: data.dropoff?.airportCode || null,
                    airline: null, flightNumber: null, terminal: null, flightTime: null,
                  },
                  travelers: {
                    numTravelers: data.travelers || 1,
                    numBags: data.bags || 0,
                    numHandBags: data.handbags || 0,
                    numChildSeats: data.childSeats || 0,
                  },
                  estimatedAmount: data.totalAmount || 0,
                  requestedRideTypeCode: null,
                }],
              };
              return { type: 'quote', data: normalized };
            }
            // Standard format with quoteId/bookingId
            const type = data.bookingId || data.bookingNumber ? 'booking'
              : isCharterCard(data) ? 'charter'
              : data.quoteId || data.quoteNumber ? 'quote' : null;
            return type ? { type, data } : null;
          })
          .filter(Boolean);
        if (items.length > 0) return { cards: items, trailing: null };
      }
    } catch {
      // not valid JSON, continue
    }
  }

  // Strip DISPLAY_CARD: prefix if present
  const stripped = content.replace(/^DISPLAY_CARD:/i, '').trim();

  // Try JSON fullDetails= format
  if (stripped.includes('fullDetails=')) {
    content = stripped;
  }
  if (content.includes('fullDetails=')) {
    const items = [];
    const parts = content.split('fullDetails=');

    for (let i = 1; i < parts.length; i++) {
      const raw = parts[i].trim();
      const opener = raw[0];

      let trailing = null;

      // Handle array: fullDetails=[{...},{...}] — parse intact, or SALVAGE a truncated one.
      if (opener === '[') {
        const lastBracket = raw.lastIndexOf(']');
        trailing = lastBracket !== -1 ? (raw.substring(lastBracket + 1).trim() || null) : null;
        let arr = null;
        if (lastBracket !== -1) {
          try { arr = JSON.parse(raw.substring(0, lastBracket + 1)); } catch { arr = null; }
        }
        if (!Array.isArray(arr)) arr = salvageJSON(raw);   // truncated / unparseable
        if (Array.isArray(arr)) {
          for (const data of arr) {
            const type = cardTypeOf(data);
            if (type) items.push({ type, data });
          }
        }
        if (items.length > 0) {
          // Charter quote cards chain a follow-up prompt (confirm / next ask) under the card, so we
          // render the trailing PROSE — but never JSON debris (a route-mode partial re-emit).
          return { cards: items, trailing: cleanTrailing(trailing) || charterFollowup(items) };
        }
        continue;
      }

      // Handle single object: fullDetails={...} — parse intact, or SALVAGE a truncated one (the final
      // SSE event can be dropped mid-JSON, losing the closing brace) so the card still renders.
      if (opener === '{') {
        let depth = 0;
        let end = -1;
        for (let j = 0; j < raw.length; j++) {
          if (raw[j] === '{') depth++;
          else if (raw[j] === '}') {
            depth--;
            if (depth === 0) { end = j; break; }
          }
        }
        let data = null;
        if (end !== -1) {
          trailing = raw.substring(end + 1).trim() || null;
          try { data = JSON.parse(raw.substring(0, end + 1)); }
          catch { data = salvageJSON(raw.substring(0, end + 1)); }
        } else {
          data = salvageJSON(raw);   // no matching brace -> truncated tail
        }
        if (data && !Array.isArray(data)) {
          const type = cardTypeOf(data);
          if (type) items.push({ type, data });
        }
      }
    }
    if (items.length > 0) {
      // Check for trailing text after last JSON block
      const lastPart = parts[parts.length - 1].trim();
      const lastBrace = Math.max(lastPart.lastIndexOf('}'), lastPart.lastIndexOf(']'));
      const trailingText = lastBrace !== -1 ? lastPart.substring(lastBrace + 1).trim() : null;
      // Charter quote cards chain a follow-up prompt (confirm / next ask) under the card, so we render
      // the trailing PROSE — but never JSON debris (a route-mode partial re-emit).
      return { cards: items, trailing: cleanTrailing(trailingText) || charterFollowup(items) };
    }
    // A DISPLAY_CARD/fullDetails token was present but produced no card even after salvage — return a
    // sentinel so the caller shows a neutral line, NEVER the raw JSON string.
    return { cards: [], trailing: null, hadCard: true };
  }

  // Try plain text booking format (single or multiple)
  if (content.match(/Booking\s*Number[:\s]*(B-[\w]+)/i)) {
    const bookingBlocks = content.split(/(?=(?:\d+\.\s*)?Booking\s*Number[:\s]*B-)/i);
    const bookingItems = [];

    for (const block of bookingBlocks) {
      const bnMatch = block.match(/Booking\s*Number[:\s]*(B-[\w]+)/i);
      if (!bnMatch) continue;

      const clean = block.replace(/^[\s]*[-*◦•]\s*/gm, '');
      const get = (pattern) => {
        const m = clean.match(pattern);
        return m ? m[1].trim() : null;
      };

      const pickupLoc = get(/Pickup\s*(?:Location)?[:\s]*([^\n]+)/i) || '';
      const dropoffLoc = get(/Drop\s*-?\s*off\s*(?:Location)?[:\s]*([^\n]+)/i) || '';
      const dateTime = get(/(?:Pickup\s*)?Date\s*(?:and|&)?\s*Time[:\s]*([^\n]+)/i);
      const travelers = parseInt(get(/(?:Number of\s*)?Travelers?[:\s]*(\d+)/i) || '1');
      const bags = parseInt(get(/(?:Number of\s*)?Bags?[:\s]*(\d+)/i) || '0');
      const amount = parseFloat(get(/(?:Total\s*)?Amount[:\s]*\$?([\d.]+)/i) || '0');
      const status = get(/Status[:\s]*(\w+)/i) || 'CONFIRMED';

      const parseLoc = (str) => {
        if (!str) return { addressLine1: null, city: null, state: null };
        const parts = str.trim().split(/[,\s]+/).filter(Boolean);
        const state = parts.length > 1 ? parts.pop() : null;
        const city = parts.join(' ') || null;
        return { addressLine1: null, city, state };
      };

      const booking = {
        bookingId: 0,
        bookingNumber: bnMatch[1],
        status,
        currencyCode: 'USD',
        totalAmount: amount,
        subtotalAmount: amount,
        taxAmount: 0, feeAmount: 0, discountAmount: 0,
        trips: [{
          tripSequence: 1,
          pickup: { ...parseLoc(pickupLoc), datetime: dateTime, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
          dropoff: { ...parseLoc(dropoffLoc), datetime: null, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
          travelers: { numTravelers: travelers, numBags: bags, numHandBags: 0, numChildSeats: 0 },
          tripStatus: status,
          estimatedAmount: amount,
        }],
      };
      bookingItems.push({ type: 'booking', data: booking });
    }

    if (bookingItems.length > 0) {
      const trailingMatch = content.match(/(?:If you need.+|Would you like.+|Feel free.+|Let me know.+)$/im);
      return { cards: bookingItems, trailing: trailingMatch ? trailingMatch[0] : null };
    }
  }

  // Try plain text quote listing format
  // Match both "Quote ID 25" and "Quote Number: Q-xxx" patterns
  const quoteBlocks = content.split(/(?=(?:\d+\.\s*)?Quote (?:ID[:\s]*\d+|Number[:\s]*Q-))/i);
  if (quoteBlocks.length < 2) return null;

  const items = [];
  let trailing = '';

  for (const block of quoteBlocks) {
    const idMatch = block.match(/Quote ID[:\s]*(\d+)/i);
    const numMatch = block.match(/Quote Number[:\s]*(Q-[\w]+)/i);
    if (!idMatch && !numMatch) {
      if (items.length > 0) trailing += block;
      continue;
    }

    // Strip markdown bullets/list markers for cleaner matching
    const clean = block.replace(/^[\s]*[-*◦•]\s*/gm, '');

    const get = (pattern) => {
      const m = clean.match(pattern);
      return m ? m[1].trim() : null;
    };

    const quote = {
      quoteId: idMatch ? parseInt(idMatch[1]) : 0,
      quoteNumber: numMatch ? numMatch[1] : get(/Quote Number[:\s]*(Q-[\w]+)/i),
      totalAmount: parseFloat(get(/Amount[:\s]*\$?([\d.]+)/i) || get(/Total[:\s]*\$?([\d.]+)/i) || '0'),
      currencyCode: get(/Amount[:\s]*\$?[\d.]+\s*(\w{3})/i) || 'USD',
      status: get(/Status[:\s]*(\w+)/i) || 'DRAFT',
      subtotalAmount: parseFloat(get(/Amount[:\s]*\$?([\d.]+)/i) || '0'),
      taxAmount: 0,
      feeAmount: 0,
      discountAmount: 0,
      trips: [],
    };

    // Parse trip info
    const from = get(/(?:Source|From)[:\s]*([^\n]+)/i);
    const to = get(/(?:Destination|To)[:\s]*([^\n]+)/i);
    const dateTime = get(/Date\s*[&]?\s*Time[:\s]*([^\n]+)/i)
      || get(/Date\s*&amp;\s*Time[:\s]*([^\n]+)/i)
      || get(/Pickup\s*(?:Date|Time|DateTime)[:\s]*([^\n]+)/i)
      || get(/Pickup[:\s]*([^\n]*\d{4}[^\n]*)/i)
      || get(/Created[:\s]*([^\n]+\d{4}[^\n]*)/i);
    const travelers = parseInt(get(/Travelers?[:\s]*(\d+)/i) || '1');
    const bags = parseInt(get(/(?:Checked\s*)?Bags?[:\s]*(\d+)/i) || '0');
    const handBags = parseInt(get(/Hand\s*Bags?[:\s]*(\d+)/i) || '0');
    const childSeats = parseInt(get(/Child\s*Seats?[:\s]*(\d+)/i) || '0');

    if (from || to) {
      const parseLoc = (str) => {
        if (!str) return { addressLine1: null, city: null, state: null };
        const parts = str.trim().split(/[,\s]+/).filter(Boolean);
        const state = parts.length > 1 ? parts.pop() : null;
        const city = parts.join(' ') || null;
        return { addressLine1: null, city, state };
      };

      quote.trips.push({
        tripSequence: 1,
        pickup: { ...parseLoc(from), datetime: dateTime, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        dropoff: { ...parseLoc(to), datetime: null, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        travelers: { numTravelers: travelers, numBags: bags, numHandBags: handBags, numChildSeats: childSeats },
        estimatedAmount: quote.totalAmount,
        requestedRideTypeCode: null,
      });
    }

    items.push({ type: 'quote', data: quote });
  }

  if (items.length > 0) {
    // Capture trailing text
    const trailingMatch = content.match(/(?:Would you like.+|Need further.+|Let me know.+|Would you like to proceed.+)$/im);
    if (trailingMatch) trailing = trailingMatch[0];
    return { cards: items, trailing: trailing || null };
  }

  // Try natural language booking confirmation
  // e.g. "Your ride has been confirmed/booked ... Booking Number: B-xxx ... from Moline, IL to Chicago, IL ... $558.63"
  const bookingNumInText = content.match(/\bB-[A-Z0-9]{6,}\b/);
  const isBookingText = content.match(/\b(?:book(?:ed|ing)|confirm(?:ed|ation)?|ride\s+(?:has\s+been\s+)?(?:booked|confirmed)|booking\s+number|booking\s+confirmed)\b/i);
  if (bookingNumInText && isBookingText) {
    const amt = content.match(/\$([\d.]+)/);
    const fromTo = content.match(/from\s+(.+?)\s+to\s+(.+?)(?:\s+on\s+|\s+for\s+|\s+with\s+|\s+is\s+|\.\s*)/i);
    const dateM = content.match(/on\s+([\w\s,]+?(?:\d{1,2}(?:st|nd|rd|th)?)?)\s+at\s+([\d:]+\s*[APap][Mm]?)/i);
    const trav = content.match(/(\d+)\s*(?:adults?|travelers?|passengers?|people)/i);
    const bags = content.match(/(\d+)\s*(?:checked\s*)?bags?\b/i);
    const status = content.match(/\b(confirmed|booked|cancelled)\b/i);

    const parseLoc = (str) => {
      if (!str) return { addressLine1: null, city: null, state: null };
      const stMatch = str.match(/,\s*([A-Z]{2})\s*$/);
      if (stMatch) {
        const before = str.replace(/,\s*[A-Z]{2}\s*$/, '').trim();
        const cityM = before.match(/,\s*([^,]+)$/);
        return { addressLine1: null, city: cityM ? cityM[1].trim() : before, state: stMatch[1] };
      }
      const parts = str.split(/[,\s]+/).filter(Boolean);
      const st = parts.length > 1 && parts[parts.length - 1].length === 2 ? parts.pop() : null;
      return { addressLine1: null, city: parts.join(' ') || null, state: st };
    };

    const booking = {
      bookingId: 0,
      bookingNumber: bookingNumInText[0],
      status: status ? status[1].toUpperCase() : 'CONFIRMED',
      currencyCode: 'USD',
      totalAmount: amt ? parseFloat(amt[1]) : 0,
      subtotalAmount: amt ? parseFloat(amt[1]) : 0,
      taxAmount: 0, feeAmount: 0, discountAmount: 0,
      trips: [{
        tripSequence: 1,
        pickup: { ...(fromTo ? parseLoc(fromTo[1].trim()) : { addressLine1: null, city: null, state: null }), datetime: dateM ? dateM[0].replace(/^on\s+/i, '').trim() : null, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        dropoff: { ...(fromTo ? parseLoc(fromTo[2].trim()) : { addressLine1: null, city: null, state: null }), datetime: null, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        travelers: { numTravelers: trav ? parseInt(trav[1]) : 1, numBags: bags ? parseInt(bags[1]) : 0, numHandBags: 0, numChildSeats: 0 },
        tripStatus: status ? status[1].toUpperCase() : 'CONFIRMED',
        estimatedAmount: amt ? parseFloat(amt[1]) : 0,
      }],
    };

    const trailingB = content.match(/(?:If you need.+|Would you like.+|Feel free.+|Let me know.+|Do you want.+)$/im);
    return { cards: [{ type: 'booking', data: booking }], trailing: trailingB ? trailingB[0] : null };
  }

  // Try natural language quote summary
  // e.g. "The estimated quote ... from Moline, IL to Chicago, IL ... is $558.63 in a Sedan"
  const amountMatch = content.match(/\$([\d.]+)/);
  const fromToMatch = content.match(/from\s+(.+?)\s+to\s+(.+?)(?:\s+on\s+|\s+for\s+|\s+with\s+|\s+is\s+)/i);
  if (amountMatch && fromToMatch) {
    const amount = parseFloat(amountMatch[1]);
    const fromStr = fromToMatch[1].trim().replace(/,\s*$/, '');
    const toStr = fromToMatch[2].trim().replace(/,\s*$/, '');
    const dateMatch = content.match(/on\s+([\w\s,]+?(?:\d{1,2}(?:st|nd|rd|th)?)?)\s+at\s+([\d:]+\s*[APap][Mm]?)/i)
      || content.match(/on\s+([\w\s,]+?\d{4}[^.]*)/i);
    const dateTime = dateMatch ? dateMatch[0].replace(/^on\s+/i, '').trim() : null;
    const travelersMatch = content.match(/(\d+)\s*(?:adults?|travelers?|passengers?|people)/i);
    const bagsMatch = content.match(/(\d+)\s*(?:checked\s*)?bags?\b/i);
    const handBagsMatch = content.match(/(\d+)\s*hand\s*bags?/i);
    const rideTypeMatch = content.match(/(?:in|with)\s+(?:a\s+)?(Sedan|SUV|Van|Big Van|Sprinter)/i);

    const parseLoc = (str) => {
      if (!str) return { addressLine1: null, city: null, state: null };
      // Handle "O'Hare International Airport (ORD), Chicago, IL" -> city: Chicago, state: IL
      const stateMatch = str.match(/,\s*([A-Z]{2})\s*$/);
      if (stateMatch) {
        const beforeState = str.replace(/,\s*[A-Z]{2}\s*$/, '').trim();
        const cityMatch = beforeState.match(/,\s*([^,]+)$/);
        const city = cityMatch ? cityMatch[1].trim() : beforeState;
        return { addressLine1: null, city, state: stateMatch[1] };
      }
      const parts = str.split(/[,\s]+/).filter(Boolean);
      const state = parts.length > 1 && parts[parts.length - 1].length === 2 ? parts.pop() : null;
      return { addressLine1: null, city: parts.join(' ') || null, state };
    };

    const quoteNumMatch = content.match(/Q-[\w]+/);
    const quote = {
      quoteId: 0,
      quoteNumber: quoteNumMatch ? quoteNumMatch[0] : '',
      status: 'DRAFT',
      currencyCode: 'USD',
      totalAmount: amount,
      subtotalAmount: amount,
      taxAmount: 0, feeAmount: 0, discountAmount: 0,
      trips: [{
        tripSequence: 1,
        pickup: { ...parseLoc(fromStr), datetime: dateTime, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        dropoff: { ...parseLoc(toStr), datetime: null, isAirport: false, airportCode: null, airline: null, flightNumber: null, terminal: null, flightTime: null },
        travelers: {
          numTravelers: travelersMatch ? parseInt(travelersMatch[1]) : 1,
          numBags: bagsMatch ? parseInt(bagsMatch[1]) : 0,
          numHandBags: handBagsMatch ? parseInt(handBagsMatch[1]) : 0,
          numChildSeats: 0,
        },
        estimatedAmount: amount,
        requestedRideTypeCode: rideTypeMatch ? rideTypeMatch[1].toUpperCase() : null,
      }],
    };

    const trailingNl = content.match(/(?:Would you like.+|Need further.+|Let me know.+|Would you like to proceed.+)$/im);
    return { cards: [{ type: 'quote', data: quote }], trailing: trailingNl ? trailingNl[0] : null };
  }

  return null;
}

function formatDateTime(dt) {
  if (!dt) return '-';
  const d = new Date(dt);
  if (isNaN(d.getTime())) return dt; // already human-readable, return as-is
  return d.toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

function formatLocation(loc) {
  let addr = loc.addressLine1;
  // Skip addressLine1 if it's just the airport code repeated (e.g. "ORD Airport")
  if (addr && loc.airportCode && addr.toLowerCase().includes(loc.airportCode.toLowerCase())) {
    addr = null;
  }
  const parts = [addr, loc.city, loc.state].filter(Boolean);
  let result = parts.join(', ') || '-';
  if (loc.airportCode) {
    result += ` (${loc.airportCode})`;
  }
  return result;
}

function TripBlock({ trip, statusBadge }) {
  const travelers = trip.travelers?.numTravelers ?? 0;
  const bags = trip.travelers?.numBags ?? 0;
  const hasBreakdown = trip.distanceMiles != null || trip.perMileRate != null;
  return (
    <div className="mt-3 bg-white rounded-card border border-border p-4">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-11 font-medium text-body/60 uppercase tracking-brand">
          Trip {trip.tripSequence}
        </span>
        {trip.requestedRideTypeCode && (
          <span className="inline-flex items-center text-11 font-medium px-2.5 py-0.5 rounded-full border bg-primary/5 text-primary border-primary/20">
            {trip.requestedRideTypeCode}
          </span>
        )}
        {statusBadge}
      </div>

      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <span className="w-2.5 h-2.5 mt-1.5 rounded-full bg-primary shrink-0" />
          <div className="min-w-0">
            <div className="text-11 font-medium text-body/60 uppercase tracking-brand">Pickup</div>
            <div className="text-13 font-medium text-body mt-0.5">{formatLocation(trip.pickup)}</div>
            <div className="text-11 font-mono text-body/60 mt-0.5">{formatDateTime(trip.pickup?.datetime)}</div>
          </div>
        </div>
        <div className="ml-[4px] w-0.5 h-4 bg-border" />
        <div className="flex items-start gap-3">
          <span className="w-2.5 h-2.5 mt-1.5 rounded-full bg-accent shrink-0" />
          <div className="min-w-0">
            <div className="text-11 font-medium text-body/60 uppercase tracking-brand">Dropoff</div>
            <div className="text-13 font-medium text-body mt-0.5">{formatLocation(trip.dropoff)}</div>
          </div>
        </div>
      </div>

      <div className="flex items-center gap-4 mt-3 pt-3 border-t border-border text-11 text-body/60">
        <span>{travelers} traveler{travelers !== 1 ? 's' : ''}</span>
        <span>{bags} bag{bags !== 1 ? 's' : ''}</span>
      </div>

      {hasBreakdown && (
        <div className="mt-3 pt-3 border-t border-border space-y-1.5 text-11">
          {trip.distanceMiles != null && (
            <div className="flex justify-between">
              <span className="text-body/60">Distance</span>
              <span className="font-mono text-body">{Number(trip.distanceMiles).toFixed(2)} mi</span>
            </div>
          )}
          {trip.perMileRate != null && (
            <div className="flex justify-between">
              <span className="text-body/60">Rate</span>
              <span className="font-mono text-body">${Number(trip.perMileRate).toFixed(2)}/mi</span>
            </div>
          )}
          {trip.distanceMiles != null && trip.perMileRate != null && (
            <div className="flex justify-between">
              <span className="text-body/60">Subtotal</span>
              <span className="font-mono text-body">${(Number(trip.distanceMiles) * Number(trip.perMileRate)).toFixed(2)}</span>
            </div>
          )}
          {trip.airportFee != null && Number(trip.airportFee) > 0 && (trip.pickup?.isAirport || trip.dropoff?.isAirport) && (
            <div className="flex justify-between">
              <span className="text-body/60">Airport fee</span>
              <span className="font-mono text-body">${Number(trip.airportFee).toFixed(2)}</span>
            </div>
          )}
          {trip.estimatedAmount != null && (
            <div className="flex justify-between font-semibold">
              <span className="text-body">Trip total</span>
              <span className="font-mono text-primary">${Number(trip.estimatedAmount).toFixed(2)}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SummaryRow({ label, value, negative }) {
  return (
    <div className="flex justify-between text-11">
      <span className="text-body/60">{label}</span>
      <span className="font-mono text-body">{negative ? '-' : ''}${value.toFixed(2)}</span>
    </div>
  );
}

function QuoteSummaryCard({ quote }) {
  return (
    <div className="bg-surface border border-border rounded-card p-5 my-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-primary">Quote <span className="font-mono">{quote.quoteNumber}</span></span>
        <StatusBadge status={quote.status} />
      </div>

      <div className="flex items-baseline justify-between py-3 border-y border-border">
        <span className="text-11 font-medium text-body/60 uppercase tracking-brand">Total</span>
        <span className="text-3xl font-bold text-primary font-mono">
          ${quote.totalAmount.toFixed(2)} <span className="text-sm font-medium text-body/60">{quote.currencyCode}</span>
        </span>
      </div>

      <div className="mt-3 space-y-1.5">
        {quote.subtotalAmount !== quote.totalAmount && (
          <SummaryRow label="Subtotal" value={quote.subtotalAmount} />
        )}
        {quote.taxAmount > 0 && <SummaryRow label="Tax" value={quote.taxAmount} />}
        {quote.feeAmount > 0 && <SummaryRow label="Fees" value={quote.feeAmount} />}
        {quote.discountAmount > 0 && <SummaryRow label="Discount" value={quote.discountAmount} negative />}
      </div>

      {quote.trips && quote.trips.map((trip, i) => (
        <TripBlock key={i} trip={trip} />
      ))}
    </div>
  );
}

function BookingSummaryCard({ booking }) {
  return (
    <div className="bg-surface border border-border border-l-4 border-l-success rounded-card p-5 my-2">
      <div className="flex items-center justify-between mb-3">
        <span className="text-base font-semibold text-primary">Booking <span className="font-mono">{booking.bookingNumber}</span></span>
        <StatusBadge status={booking.status} />
      </div>

      <div className="flex items-baseline justify-between py-3 border-y border-border">
        <span className="text-11 font-medium text-body/60 uppercase tracking-brand">Total</span>
        <span className="text-3xl font-bold text-primary font-mono">
          ${booking.totalAmount.toFixed(2)} <span className="text-sm font-medium text-body/60">{booking.currencyCode}</span>
        </span>
      </div>

      <div className="mt-3 space-y-1.5">
        {booking.subtotalAmount !== booking.totalAmount && (
          <SummaryRow label="Subtotal" value={booking.subtotalAmount} />
        )}
        {booking.taxAmount > 0 && <SummaryRow label="Tax" value={booking.taxAmount} />}
        {booking.feeAmount > 0 && <SummaryRow label="Fees" value={booking.feeAmount} />}
        {booking.discountAmount > 0 && <SummaryRow label="Discount" value={booking.discountAmount} negative />}
        {(booking.quoteNumber || booking.quoteId) && (
          <div className="flex justify-between text-11">
            <span className="text-body/60">Quote</span>
            <span className="font-mono text-body">{booking.quoteNumber || `#${booking.quoteId}`}</span>
          </div>
        )}
      </div>

      {booking.trips && booking.trips.map((trip, i) => (
        <TripBlock
          key={i}
          trip={trip}
          statusBadge={trip.tripStatus ? <StatusBadge status={trip.tripStatus} /> : null}
        />
      ))}
    </div>
  );
}

function ErrorCard({ error }) {
  return (
    <div className="bg-danger-50 border border-danger/30 rounded-card p-5 my-2">
      <div className="flex items-center gap-2 mb-2">
        <IconWarning size={16} className="text-danger" />
        <span className="text-base font-semibold text-danger">Request failed</span>
      </div>
      <p className="text-13 text-danger leading-relaxed">{error.message}</p>
    </div>
  );
}

function FeedbackControl({ onFeedback, isCharter }) {
  const [state, setState] = React.useState('idle'); // idle | form | submitted
  const [rating, setRating] = React.useState(0);
  const [text, setText] = React.useState('');
  const formRef = React.useRef(null);

  React.useEffect(() => {
    if (state === 'form' && formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  }, [state]);

  if (state === 'submitted') {
    return <div className="text-13 text-success font-medium mt-1.5 pl-0.5">Thanks for your feedback</div>;
  }

  if (state === 'form') {
    return (
      <div className="mt-1.5 bg-surface border border-border rounded-card p-3 max-w-[300px]" ref={formRef}>
        <div className="flex gap-0.5 mb-2">
          {[1, 2, 3, 4, 5].map(n => (
            <button
              key={n}
              className={cx(
                'p-0.5 transition-colors hover:text-accent',
                n <= rating ? 'text-accent' : 'text-body/20'
              )}
              onClick={() => setRating(n)}
              aria-label={`${n} star${n !== 1 ? 's' : ''}`}
            >
              <Star size={18} fill={n <= rating ? 'currentColor' : 'none'} />
            </button>
          ))}
        </div>
        <textarea
          className="w-full border border-border rounded-btn px-2.5 py-2 text-13 resize-none outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
          placeholder="Tell us what went wrong..."
          value={text}
          onChange={e => setText(e.target.value)}
          rows={2}
        />
        <div className="flex justify-end gap-2 mt-2">
          <button className="text-11 text-body/60 px-3 py-1.5 hover:text-body transition-colors" onClick={() => { setState('idle'); setRating(0); setText(''); }}>Cancel</button>
          <button
            className="bg-primary text-white rounded-btn text-11 font-semibold px-3.5 py-1.5 hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            disabled={rating === 0}
            onClick={() => { onFeedback(rating, text); setState('submitted'); }}
          >
            Submit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex gap-1 mt-1 pl-0.5">
      <button className={cx('rounded-btn p-1.5 transition-colors', isCharter ? 'text-peoria-muted hover:text-peoria-red hover:bg-black/5' : 'text-body/40 hover:text-primary hover:bg-surface')} onClick={() => { onFeedback(5, ''); setState('submitted'); }} title="Good response" aria-label="Good response"><ThumbsUp size={14} /></button>
      <button className={cx('rounded-btn p-1.5 transition-colors', isCharter ? 'text-peoria-muted hover:text-danger hover:bg-black/5' : 'text-body/40 hover:text-danger hover:bg-surface')} onClick={() => setState('form')} title="Bad response" aria-label="Bad response"><ThumbsDown size={14} /></button>
    </div>
  );
}

// Client-side typewriter: progressively reveals `text` when `enabled`. The deterministic charter
// planner returns its reply as one blob (no token deltas), so this animates it out to *look* streamed.
// Length-normalized pace (~1.4s cap) so long replies never drag. When disabled (R3, or a non-last /
// card-bearing message) it returns the full text immediately. If `text` grows (real deltas), the
// reveal simply continues from where it is.
function useTypewriter(text, enabled) {
  const [shown, setShown] = useState(enabled ? '' : text);
  const idxRef = useRef(enabled ? 0 : text.length);
  useEffect(() => {
    if (!enabled) { idxRef.current = text.length; setShown(text); return; }
    const total = text.length;
    if (idxRef.current > total) idxRef.current = 0; // target replaced/shrank — restart
    let cancelled = false;
    let timer = null;
    const inc = Math.max(1, Math.ceil(total / 90)); // ~90 ticks × 16ms ≈ 1.4s regardless of length
    const step = () => {
      if (cancelled) return;
      if (idxRef.current >= total) { setShown(text); return; }
      idxRef.current = Math.min(total, idxRef.current + inc);
      setShown(text.slice(0, idxRef.current));
      timer = setTimeout(step, 16);
    };
    step();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
  }, [text, enabled]);
  return shown;
}

function ChatMessage({ message, onFeedback, onRouteRequest, onEditRoute, onAction, superseded, isCharter, streamIn }) {
  const isUser = message.role === 'user';

  // Computed before the route-card early return so the typewriter hook below always runs (rules of hooks).
  // Strip the ACTIONS token too so the chip markup never bleeds into the visible bubble / card parse.
  const displayContentEarly = !isUser
    ? stripActionsToken(stripRouteToken(stripFlightInfoToken(message.content)))
    : message.content;
  const parsedEarly = !isUser ? parseFullDetails(displayContentEarly) : null;
  const hasCardsEarly = !!(parsedEarly && ((parsedEarly.cards && parsedEarly.cards.length > 0) || parsedEarly.hadCard));
  // Only plain, marker-free assistant text streams — cards/route asks render instantly (never type
  // out partial JSON/markers).
  const streamEnabled = !!streamIn && !!isCharter && !isUser && !hasCardsEarly && !superseded;
  const typedText = useTypewriter(displayContentEarly || '', streamEnabled);
  const isStreaming = streamEnabled && typedText.length < (displayContentEarly || '').length;

  // Peoria charter: the rider just set/updated their route in the map popup — render a verified-route
  // receipt as their turn (instead of the raw "My charter route — pickup from…" sentence the agent
  // receives underneath), so they can see exactly what was captured. See CustomerApp onConfirm.
  if (isUser && message.route) {
    return (
      <div className="flex flex-col mb-4 px-4 items-end">
        <RouteCard route={message.route} updated={message.routeUpdated} />
      </div>
    );
  }

  // Reuse the values computed above the route-card early return (so the typewriter hook always runs).
  const displayContent = displayContentEarly;
  const parsed = parsedEarly;
  const hasContent = displayContent && displayContent.trim();
  // ONE canonical quote: this is an OLDER charter estimate card and a newer one renders below it, so
  // collapse it to a single muted line instead of a second full quote card. Only the latest shows.
  const isSupersededCharterCard = !isUser && superseded
    && parsed && Array.isArray(parsed.cards) && parsed.cards.some((c) => c.type === 'charter');
  // Peoria charter: this assistant turn is asking (only) for the trip's route, so the map popup is
  // offered INSIDE this bubble — right under the question it belongs to — rather than as a detached
  // card below the thread. onRouteRequest is passed only for the active route ask (see CustomerApp).
  const routeReq = !isUser && onRouteRequest ? detectRouteRequest(message.content) : null;
  const showRouteChip = !!routeReq;
  // Peoria charter: the contextual action chips for THIS turn (Continue / Edit itinerary / Add stop /
  // Send request). onAction is passed only for the latest assistant turn (see CustomerApp), so older
  // bubbles never keep stale chips.
  const actionIds = !isUser && onAction ? detectActions(message.content) : null;
  // When the seed already carries an endpoint, this ask is EDITING an existing route (a chat correction
  // or a post-quote change reopens the SAME form) — label the chip "Update" so it never reads as a new,
  // second route form. A first-time ask (empty seed) stays "Set your route".
  const routeHasSeed = !!(routeReq && Array.isArray(routeReq.legs) && routeReq.legs.some(
    (l) => (l && l.pickup && (l.pickup.city || l.pickup.address))
        || (l && l.destination && (l.destination.city || l.destination.address))));

  return (
    <div className={`flex flex-col mb-4 px-4 ${isUser ? 'items-end' : 'items-start'}`}>
      {!isUser && (
        <div className="flex items-center gap-1.5 mb-1">
          <div className={`w-8 h-8 rounded-full text-white flex items-center justify-center shadow-card ${isCharter ? 'bg-peoria-red' : 'bg-primary'}`}>
            <IconBot size={16} />
          </div>
          <span className={`text-sm font-semibold ${isCharter ? 'text-peoria-text' : 'text-primary'}`}>Assistant</span>
        </div>
      )}
      <div
        className={
          isUser
            ? `max-w-[80%] px-4 py-3 rounded-card rounded-br-md text-white text-sm leading-relaxed break-words shadow-card ${isCharter ? 'bg-peoria-red' : 'bg-primary'}`
            : `max-w-[80%] px-4 py-3 rounded-card rounded-bl-md text-sm leading-relaxed break-words shadow-card ${isCharter ? 'bg-peoria-surface text-peoria-text border border-peoria-border' : 'bg-white text-body border border-border'}`
        }
      >
        {isUser ? (
          <p>{message.content}</p>
        ) : isSupersededCharterCard ? (
          <p className={`text-sm italic ${isCharter ? 'text-peoria-muted' : 'text-body/50'}`}>Estimate updated — see the latest below.</p>
        ) : parsed && parsed.cards && parsed.cards.length > 0 ? (
          <>
            {parsed.cards.map((item, i) => {
              return item.type === 'error' ? (
                <ErrorCard key={i} error={item.data} />
              ) : item.type === 'booking' ? (
                <BookingSummaryCard key={i} booking={item.data} />
              ) : item.type === 'charter' ? (
                // Per-leg edit: the card itself decides which leg each "Edit route" opens — a two-leg
                // trip's card reads its legLabel ("Leg 2 of 2"); a round trip renders BOTH an outbound
                // and a return edit (leg 1 / 'return'). We pass the raw leg-aware handler straight down.
                <CharterQuoteCard
                  key={i}
                  quote={item.data}
                  onEditRoute={onEditRoute}
                />
              ) : item.type === 'confirmation' ? (
                <ConfirmationCard key={i} data={item.data} />
              ) : (
                <QuoteSummaryCard key={i} quote={item.data} />
              );
            })}
            {parsed.cards.filter((c) => c.type === 'quote' && c.data.status?.toUpperCase() === 'DRAFT').length > 1 && (
              <div className={`mt-3 px-4 py-3 rounded-btn text-13 font-medium text-center ${isCharter ? 'bg-peoria-red/10 border border-peoria-red/30 text-peoria-text' : 'bg-primary/5 border border-primary/20 text-primary'}`}>
                Would you like to book any of these quotes or need help with something else?
              </div>
            )}
          </>
        ) : parsed && parsed.hadCard ? (
          // A card was sent but couldn't be parsed/salvaged — show a neutral line, never raw JSON.
          <p className={`text-sm ${isCharter ? 'text-peoria-muted' : 'text-body/70'}`}>Putting your estimate together…</p>
        ) : isStreaming ? (
          // Client-side typewriter: reveal plain text as it "types". Render as plain text (not markdown)
          // while streaming to avoid flicker on partial tokens; swap to markdown once fully revealed.
          <p className="text-sm leading-relaxed whitespace-pre-wrap my-2">
            {typedText}
            <span className="inline-block w-[2px] h-[1em] -mb-[2px] ml-0.5 bg-peoria-red align-middle animate-caret-blink" aria-hidden="true" />
          </p>
        ) : (
          <div className={`prose prose-sm max-w-none prose-p:my-2 prose-p:last:mb-0 ${isCharter ? 'prose-invert' : 'prose-slate'}`}>
            <ReactMarkdown components={MD_COMPONENTS}>{displayContent}</ReactMarkdown>
          </div>
        )}
        {showRouteChip && (
          <button type="button" className="route-chip route-chip--inbubble" onClick={onRouteRequest}>
            <span className="route-chip-icon"><MapPin size={18} strokeWidth={2.25} /></span>
            <span className="route-chip-text">
              <span className="route-chip-title">{routeHasSeed ? 'Update your route' : 'Set your route'}</span>
              <span className="route-chip-sub">
                {/* Shuttle asks are a two-point loop (no stops) — say so, in the shuttle flow's own words. */}
                {routeReq && routeReq.leg === 'shuttle'
                  ? (routeHasSeed ? "Review & adjust the shuttle's pickup and drop-off points"
                                  : "Enter the shuttle's pickup and drop-off points")
                  : (routeHasSeed ? 'Review & adjust your pickup, stops & destination'
                                  : 'Enter your pickup, stops & destination')}
              </span>
            </span>
            <ChevronRight size={18} className="route-chip-chevron" />
          </button>
        )}
      </div>
      {/* Post-quote follow-up ("adjust anything, or book?") renders as its OWN message bubble below the
          quote card — a distinct assistant message, never fused into the card's bubble. */}
      {!isUser && parsed && parsed.cards && parsed.cards.length > 0 && parsed.trailing && (
        <div className={`max-w-[80%] px-4 py-3 mt-2 rounded-card rounded-bl-md text-sm leading-relaxed break-words shadow-card ${isCharter ? 'bg-peoria-surface text-peoria-text border border-peoria-border' : 'bg-white text-body border border-border'}`}>
          <p className={`font-medium ${isCharter ? 'text-peoria-text' : 'text-primary'}`}>{parsed.trailing}</p>
        </div>
      )}
      {/* Peoria charter: contextual ACTION chips for this turn — visible one-click alternatives to typing
          ("Continue", "Edit itinerary", "Add stop", "Send request"). Only the latest assistant turn gets
          onAction, so chips never linger on old bubbles. */}
      {!isUser && actionIds && actionIds.length > 0 && (
        <div className="charter-actions">
          {actionIds.map((id) => {
            const chip = CHARTER_ACTION_CHIPS[id];
            if (!chip) return null;
            const { label, Icon } = chip;
            const primary = id === 'send_request';
            return (
              <button
                key={id}
                type="button"
                className={cx('charter-action-chip', primary && 'charter-action-chip--primary')}
                onClick={() => onAction(id)}
              >
                <Icon size={15} strokeWidth={2.25} />
                <span>{label}</span>
              </button>
            );
          })}
        </div>
      )}
      {!isUser && hasContent && onFeedback && (
        <FeedbackControl isCharter={isCharter} onFeedback={(rating, feedbackText) => onFeedback(message, rating, feedbackText)} />
      )}
    </div>
  );
}

export default ChatMessage;
